import { generateUniqueId } from "utils/general";

import {
    ATTACHMENT_STATUS,
    TIME_TO_WAIT_BETWEEN_SCANS,
} from "../../constants/generalConstants";
import {
    FILE_UPLOAD_MALWARE_FOUND,
    SERVER_COPYING_ATTACHMENT,
    SERVER_DELETING_ATTACHMENT,
    SERVER_MESSAGE_CANNOT_POST_ATTACHMENT,
    SERVER_POLLING_ATTACHMENT_ERROR,
    SERVER_UPLOAD_ATTACHMENT,
} from "../../constants/snackbarMessage";
import {
    copyingAttachmentAction,
    deleteAttachmentAction,
    deleteCopyingAttachmentsAction,
    deleteUploadingAttachmentAction,
    scanAttachmentResult,
    scanAttachmentResultCopy,
    setAvailableAttachmentsToCopyAction,
    updateAvailableAttachmentsToCopyAction,
    updateScanResultStatus,
    uploadedAttachmentAction,
    uploadingAttachmentAction,
} from "../../redux/actions/attachmentActions";
import alertDispatcher from "../../utils/alertsUtils";
import axiosApiInstance from "./../middleware/axios";
import {
    ATTACHMENTS_COPY_URL,
    ATTACHMENTS_DELETE_URL,
    ATTACHMENTS_POLLING_URL,
    ATTACHMENTS_UPLOAD_URL,
} from "./urls";

export const uploadAnAttachmentApi = async (
    formData,
    levelType,
    attachmentLine,
    fileTitle,
    description,
    fileSize,
    fileName,
    dispatch
) => {
    // uploadingId helps to keep the uniqueId for attachment until attachment is uploaded to blob
    const uploadingId = generateUniqueId();
    try {
        const uploadingAttachment = {
            uploadingId,
            levelType,
            attachmentLine,
            fileTitle,
            description,
            fileSize,
            fileName,
        };

        // This helps to show the upload progress in UI
        dispatch(uploadingAttachmentAction(uploadingAttachment));

        const response = await axiosApiInstance.post(
            ATTACHMENTS_UPLOAD_URL,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
                data: {
                    formData,
                },
            }
        );

        const attachmentData = {
            ...response.data,
            ...uploadingAttachment,
        };

        dispatch(uploadedAttachmentAction(attachmentData));
        pollForScanStatus(attachmentData, dispatch);
    } catch (error) {
        // In case of uploading fails, attachment shall be removed from the state management
        dispatch(deleteUploadingAttachmentAction(uploadingId));
        alertDispatcher(
            "uploadAttachment",
            404,
            dispatch,
            SERVER_MESSAGE_CANNOT_POST_ATTACHMENT
        );
        return;
    }
};

export const pollForScanStatus = (
    attachmentData,
    dispatch,
    fromCopy = false
) => {
    let intervalId;
    let timeoutId;
    let attachmentId = attachmentData.scanId;
    const checkStatus = async () => {
        try {
            const response = await axiosApiInstance.get(
                `${ATTACHMENTS_POLLING_URL}/${attachmentData.scanId}`
            );
            if (response.data.status === ATTACHMENT_STATUS.SCAN_FAILED) {
                clearInterval(intervalId);
                clearTimeout(timeoutId);
                dispatch(
                    deleteAttachmentAction({
                        ATTACHMENT_ID: response.data.scanId,
                    })
                );
                alertDispatcher(
                    "pollForScanStatus",
                    400,
                    dispatch,
                    FILE_UPLOAD_MALWARE_FOUND(attachmentData.fileName)
                );
            } else if (
                response.data.status === ATTACHMENT_STATUS.SCAN_COMPLETED
            ) {
                clearInterval(intervalId);
                clearTimeout(timeoutId);
                const scanData = {
                    ...response.data,
                    levelType: attachmentData.levelType,
                    attachmentLine: attachmentData.attachmentLine,
                    fileName: attachmentData.fileName,
                    fileTitle: attachmentData.fileTitle,
                    description: attachmentData.description,
                    fileSize: attachmentData.fileSize,
                    headerOrRow: attachmentData?.headerOrRow,
                };
                if (fromCopy) {
                    scanData.ATTACHMENT_ID = attachmentData.attachmentId;
                    scanData.isCopiedFile = true;
                    dispatch(updateScanResultStatus(scanData));
                } else {
                    dispatch(scanAttachmentResult(scanData));
                }
            }
        } catch (error) {
            clearInterval(intervalId);
            clearTimeout(timeoutId);
            alertDispatcher(
                "pollForScanStatus",
                404,
                dispatch,
                SERVER_POLLING_ATTACHMENT_ERROR
            );
        }
    };

    intervalId = setInterval(checkStatus, TIME_TO_WAIT_BETWEEN_SCANS);

    // Stop polling after 90 seconds
    timeoutId = setTimeout(() => {
        clearInterval(intervalId);
        eliminateAttachmentByFileId(attachmentId, dispatch, true);
        dispatch(
            deleteAttachmentAction({
                ATTACHMENT_ID: attachmentId,
            })
        );
        alertDispatcher(
            "pollForScanStatus",
            400,
            dispatch,
            `The malware scan for ${attachmentData.fileName} is taking longer than anticipated, please try again later`
        );
    }, 90000);
};

export const eliminateAttachmentByFileId = async (
    attachmentId,
    dispatch,
    noAlertOnError = false
) => {
    try {
        return await axiosApiInstance.delete(
            `${ATTACHMENTS_DELETE_URL}/${attachmentId}`
        );
    } catch (error) {
        if (!noAlertOnError) {
            alertDispatcher(
                "deleteAttachment",
                404,
                dispatch,
                SERVER_DELETING_ATTACHMENT
            );
        }
    }
};

export const includeCategoryInScanResults = (
    attachmentFiles,
    copyResponseData
) => {
    return copyResponseData.map((item) => {
        const attachmentFile = attachmentFiles.find(
            (file) => file.ATTACHMENT_ID === item.ATTACHMENT_ID
        );
        return {
            ...item,
            ATTACHMENT_ID: item.FILE_ID,
            CATEGORY: attachmentFile ? attachmentFile.CATEGORY : [],
            FILENAME: attachmentFile.FILENAME,
            copyingId: attachmentFile.copyingId,
        };
    });
};

export const copyAttachmentFiles = async (filesArray, dispatch) => {
    // Assigning unique copying id for each attachment, this helps to maintain the state
    filesArray.forEach((fileItem) => {
        fileItem.copyingId = generateUniqueId();
        fileItem.CREATED_DATE = new Date().toISOString();
    });

    try {
        // This helps to show the progress in UI while copy api call is executing
        dispatch(copyingAttachmentAction(filesArray));

        const response = await axiosApiInstance.post(
            `${ATTACHMENTS_COPY_URL}`,
            { attachments: filesArray }
        );

        const scanResultsData = includeCategoryInScanResults(
            filesArray,
            response.data.attachments
        );

        dispatch(scanAttachmentResultCopy(scanResultsData));
        // dispatch(selectToCopyAttachmentsAction([]));
        dispatch(
            updateAvailableAttachmentsToCopyAction(
                filesArray.map((a) => a.ATTACHMENT_ID),
                { isCopied: true }
            )
        );

        response.data.attachments.forEach(async (file, index) => {
            const originalFile = filesArray[index];
            const attachmentData = {
                scanId: file.FILE_ID,
                fileName: originalFile.FILENAME,
                fileTitle: originalFile.TITLE,
                headerOrRow: originalFile.headerOrRow,
                attachmentId: originalFile.ATTACHMENT_ID,
            };
            pollForScanStatus(attachmentData, dispatch, true);
        });
    } catch (error) {
        // Delete attachments from state in case of copy api call fails
        dispatch(deleteCopyingAttachmentsAction(filesArray));
        // dispatch(selectToCopyAttachmentsAction([]));
        alertDispatcher(
            "copyAttachments",
            404,
            dispatch,
            SERVER_COPYING_ATTACHMENT
        );
    }
};

export const downloadAttachments = async (url, dispatch) => {
    try {
        const response = await axiosApiInstance.get(url, {
            responseType: "blob",
        });
        return response;
    } catch (error) {
        alertDispatcher(
            "errorDownloadingFile",
            error?.response?.status,
            dispatch,
            "There was an error in downloading the file."
        );
    }
};

export const deleteAttachments = async (attachmentListToDelete, dispatch) => {
    const deletedList = [];
    attachmentListToDelete.forEach((attachment) => {
        try {
            if (attachment.isNewUpload) {
                const response = axiosApiInstance.delete(
                    `${ATTACHMENTS_DELETE_URL}/${attachment.FILE_ID}`
                );
                deletedList.push(attachment.ATTACHMENT_ID);
            }
        } catch (error) {
            alertDispatcher(
                "deleteAttachment",
                error?.response?.status,
                dispatch,
                error?.response?.data?.message
            );
        }
    });
};
